import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { authAPI } from '../services/api';
import { useToast } from '../components/ToastContainer';
import './ActiveSessionsPage.css';

interface ActiveSession {
  sessionId: string;
  sessionIdDisplay?: string;
  userId?: number;
  userEmail?: string;
  userName?: string;
  userRole?: string;
  isGuest: boolean;
  ip?: string;
  userAgent?: string;
  createdAt: string;
  lastActivity: string;
  expiresAt: string;
  currentPath?: string;
  lastAction?: string;
  requestCount?: number;
  recentPaths?: string[];
  duration: number; // seconds
  idleTime: number; // seconds
}

interface SessionStats {
  total: number;
  authenticated: number;
  guest: number;
  byRole: Record<string, number>;
}

const ActiveSessionsPage: React.FC = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<ActiveSession[]>([]);
  const [stats, setStats] = useState<SessionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [sessionActivity, setSessionActivity] = useState<Record<string, any[]>>({});
  const [loadingActivity, setLoadingActivity] = useState<Set<string>>(new Set());
  const [revokingSessions, setRevokingSessions] = useState<Set<string>>(new Set());

  const fetchSessions = useCallback(async () => {
    try {
      const response = await authAPI.getActiveSessions();
      setSessions(response.data.sessions || []);
      setStats(response.data.stats || null);
      setLoading(false);
    } catch (error: any) {
      console.error('Error fetching active sessions:', error);
      showToast('Failed to fetch active sessions', 'error');
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  useEffect(() => {
    // Set up auto-refresh if enabled
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchSessions();
      }, 15000); // Refresh every 15 seconds (reduced from 5 seconds for efficiency)

      return () => {
        clearInterval(interval);
      };
    }
  }, [autoRefresh, fetchSessions]);

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getRoleColor = (role?: string): string => {
    switch (role) {
      case 'admin': return '#ff6b6b';
      case 'supervisor': return '#4ecdc4';
      case 'limited_admin': return '#95e1d3';
      case 'user': return '#a8e6cf';
      default: return '#ddd';
    }
  };

  const toggleSessionDetails = async (sessionId: string) => {
    const isExpanded = expandedSessions.has(sessionId);
    
    if (isExpanded) {
      // Collapse
      setExpandedSessions(prev => {
        const next = new Set(prev);
        next.delete(sessionId);
        return next;
      });
    } else {
      // Expand - fetch activity
      setExpandedSessions(prev => new Set(prev).add(sessionId));
      
      if (!sessionActivity[sessionId]) {
        setLoadingActivity(prev => new Set(prev).add(sessionId));
        try {
          const response = await authAPI.getSessionActivity(sessionId);
          setSessionActivity(prev => ({
            ...prev,
            [sessionId]: response.data.activity || []
          }));
        } catch (error: any) {
          console.error('Error fetching session activity:', error);
          showToast('Failed to fetch session activity', 'error');
        } finally {
          setLoadingActivity(prev => {
            const next = new Set(prev);
            next.delete(sessionId);
            return next;
          });
        }
      }
    }
  };

  const handleRevokeSession = async (sessionId: string) => {
    if (!window.confirm('Are you sure you want to revoke this session? The user will be logged out immediately.')) {
      return;
    }

    setRevokingSessions(prev => new Set(prev).add(sessionId));
    try {
      await authAPI.revokeSession(sessionId);
      showToast('Session revoked successfully', 'success');
      // Refresh sessions list
      await fetchSessions();
      // Remove from expanded sessions if it was expanded
      setExpandedSessions(prev => {
        const next = new Set(prev);
        next.delete(sessionId);
        return next;
      });
      // Remove activity data
      setSessionActivity(prev => {
        const next = { ...prev };
        delete next[sessionId];
        return next;
      });
    } catch (error: any) {
      console.error('Error revoking session:', error);
      showToast(error.response?.data?.message || 'Failed to revoke session', 'error');
    } finally {
      setRevokingSessions(prev => {
        const next = new Set(prev);
        next.delete(sessionId);
        return next;
      });
    }
  };

  return (
    <div className="active-sessions-page">
      <div className="active-sessions-header">
        <h1>🔐 Active Sessions Monitor</h1>
        <div className="controls">
          <button 
            onClick={() => navigate('/admin/send-message')}
            className="send-message-header-btn"
          >
            📨 Send Message
          </button>
          <button 
            onClick={fetchSessions} 
            className="refresh-btn"
            disabled={loading}
          >
            🔄 Refresh
          </button>
          <label className="auto-refresh-label">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh (5s)
          </label>
        </div>
      </div>

      {loading && sessions.length === 0 ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading active sessions...</p>
        </div>
      ) : (
        <>
          {stats && (
            <div className="stats-cards">
              <div className="stat-card">
                <div className="stat-value">{stats.total}</div>
                <div className="stat-label">Total Sessions</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.authenticated}</div>
                <div className="stat-label">Authenticated</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.guest}</div>
                <div className="stat-label">Guest Sessions</div>
              </div>
              {Object.entries(stats.byRole).map(([role, count]) => (
                <div key={role} className="stat-card" style={{ borderLeftColor: getRoleColor(role) }}>
                  <div className="stat-value">{count}</div>
                  <div className="stat-label">{role.charAt(0).toUpperCase() + role.slice(1)}</div>
                </div>
              ))}
            </div>
          )}

          <div className="sessions-container">
            {sessions.length === 0 ? (
              <div className="no-sessions">
                <p>No active sessions</p>
              </div>
            ) : (
              <div className="sessions-list">
                {sessions.map((session) => (
                  <div key={session.sessionId} className="session-card">
                      <div className="session-header">
                      <div className="session-identity">
                        <span className="session-id">ID: {session.sessionIdDisplay || session.sessionId.substring(0, 8) + '...'}</span>
                        {session.isGuest ? (
                          <span className="badge guest-badge">Guest</span>
                        ) : (
                          session.userRole && (
                            <span 
                              className="badge role-badge"
                              style={{ backgroundColor: getRoleColor(session.userRole) }}
                            >
                              {session.userRole}
                            </span>
                          )
                        )}
                      </div>
                    </div>
                    
                    <div className="session-body">
                      {session.userName && (
                        <div className="session-info">
                          <strong>👤 User:</strong> {session.userName}
                        </div>
                      )}
                      {session.userEmail && (
                        <div className="session-info">
                          <strong>📧 Email:</strong> {session.userEmail}
                        </div>
                      )}
                      {session.ip && (
                        <div className="session-info">
                          <strong>🌐 IP:</strong> {session.ip}
                        </div>
                      )}
                      {session.userAgent && (
                        <div className="session-info">
                          <strong>💻 Browser:</strong> {session.userAgent.substring(0, 60)}...
                        </div>
                      )}
                      {session.currentPath && (
                        <div className="session-info highlight">
                          <strong>📍 Current Page:</strong> <code className="path-code">{session.currentPath}</code>
                        </div>
                      )}
                      {session.lastAction && (
                        <div className="session-info">
                          <strong>⚡ Last Action:</strong> <code className="action-code">{session.lastAction}</code>
                        </div>
                      )}
                      {session.requestCount && (
                        <div className="session-info">
                          <strong>📊 Requests:</strong> {session.requestCount.toLocaleString()}
                        </div>
                      )}
                      {session.recentPaths && session.recentPaths.length > 0 && (
                        <div className="session-info">
                          <strong>🕐 Recent Pages:</strong>
                          <div className="recent-paths">
                            {session.recentPaths.map((path, idx) => (
                              <span key={idx} className="path-badge">{path}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="session-info">
                        <strong>🕐 Started:</strong> {formatTime(session.createdAt)}
                      </div>
                      <div className="session-info">
                        <strong>⏱️ Duration:</strong> {formatDuration(session.duration)}
                      </div>
                      <div className="session-info">
                        <strong>💤 Idle:</strong> {formatDuration(session.idleTime)}
                      </div>
                      <div className="session-info">
                        <strong>⏰ Last Activity:</strong> {formatTime(session.lastActivity)}
                      </div>
                      
                      <div className="session-actions">
                        <button
                          className="view-activity-btn"
                          onClick={() => toggleSessionDetails(session.sessionId)}
                        >
                          {expandedSessions.has(session.sessionId) ? '▼' : '▶'} View Activity
                        </button>
                        <button
                          className="revoke-session-btn"
                          onClick={() => handleRevokeSession(session.sessionId)}
                          disabled={revokingSessions.has(session.sessionId)}
                        >
                          {revokingSessions.has(session.sessionId) ? '⏳ Revoking...' : '🚫 Revoke Session'}
                        </button>
                      </div>
                      
                      {expandedSessions.has(session.sessionId) && (
                        <div className="session-activity">
                          {loadingActivity.has(session.sessionId) ? (
                            <div className="activity-loading">Loading activity...</div>
                          ) : sessionActivity[session.sessionId]?.length > 0 ? (
                            <div className="activity-list">
                              <h4>Recent Activity ({sessionActivity[session.sessionId].length})</h4>
                              {sessionActivity[session.sessionId].slice(0, 10).map((activity: any, idx: number) => (
                                <div key={idx} className="activity-item">
                                  <div className="activity-header">
                                    <span className="activity-action">{activity.action}</span>
                                    <span className="activity-time">{formatTime(activity.timestamp)}</span>
                                  </div>
                                  {activity.resource_type && (
                                    <div className="activity-details">
                                      <span className="activity-resource">{activity.resource_type}</span>
                                      {activity.resource_id && (
                                        <span className="activity-id">ID: {activity.resource_id}</span>
                                      )}
                                      {activity.success !== undefined && (
                                        <span className={`activity-success ${activity.success ? 'success' : 'failure'}`}>
                                          {activity.success ? '✓' : '✗'}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {activity.request_path && (
                                    <div className="activity-path">
                                      {activity.request_method} {activity.request_path}
                                    </div>
                                  )}
                                </div>
                              ))}
                              {sessionActivity[session.sessionId].length > 10 && (
                                <div className="activity-more">
                                  ... and {sessionActivity[session.sessionId].length - 10} more actions
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="activity-empty">No activity recorded for this session</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ActiveSessionsPage;

